import { useState, useEffect, type SetStateAction, type Dispatch, useCallback } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '../../store';
import type { SerializedError } from '@reduxjs/toolkit';
import type { MutationDefinition } from '@reduxjs/toolkit/dist/query';

import type { UseMutation } from '@reduxjs/toolkit/dist/query/react/buildHooks';

export type UseMutationFunc<T> = (options?: UseMutationStateOptions) => [UseMutationTrigger<T>, UseMutationResult<T>];

export type UseMutationStateOptions = {
    // A method to determine the contents of `UseMutationResult`
    selectFromResult?: (result: any) => any;
    // A string used to enable shared results across hook instances which have the same key
    fixedCacheKey?: string;
};

export type UseMutationTrigger<T> = (arg: any) => Promise<{ data: T } | { error: SerializedError }> & {
    requestId: string; // A string generated by RTK Query
    abort: () => void; // A method to cancel the mutation promise
    unwrap: () => Promise<T>; // A method to unwrap the mutation call and provide the raw response/error
    reset: () => void; // A method to manually unsubscribe from the mutation call and reset the result to the uninitialized state
};

export type UseMutationResult<T> = {
    data?: T;
    error: unknown;
    originalArgs?: unknown; // Arguments passed to the latest mutation call. Not available if using the `fixedCacheKey` option
    endpointName: string;
    isUninitialized: boolean;
    isLoading: boolean;
    isSuccess: boolean;
    isError: boolean;
};


/**
 * Utility wrappers for `useMutation` hooks.
 */
export namespace Mutation {
  type UseMutationMinusToken<Def extends MutationDefinition<any, any, any, any, any>> =
      Def extends MutationDefinition<infer Params, infer Second, infer Third, infer Fourth, infer Fifth>
          ? UseMutation<
                MutationDefinition<
                    Params extends { token: string } ? Omit<Params, 'token'> : Params,
                    Second,
                    Third,
                    Fourth,
                    Fifth
                >
            >
          : never;


  export const withToken = <Def extends MutationDefinition<any, any, any, any, any>>(hook: UseMutation<Def>) => {
      return ((...args) => {
          const IdToken = useSelector((state: RootState) => state.auth.IdToken);
          const [mutateRaw, state] = hook(...args);

          // Wrap the original mutate function to automatically populate the
          // params with the token.
          const mutate = useCallback<typeof mutateRaw>(
              (first, ...rest) => mutateRaw({ ...first, token: IdToken }, ...rest),
              [IdToken, mutateRaw]
          );

          return [mutate, state] as const;
      }) as UseMutationMinusToken<Def>;
  };

}
